import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import apiClient from "../Login/apiClient";
import { useUser } from "../Login/UserContext";

const categoryOptions = [
  { value: "achievement", label: "達成認証について" },
  { value: "bug", label: "不具合報告" },
  { value: "report", label: "削除・通報" },
  { value: "other", label: "その他" },
];

function Contact() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { isLoggedIn } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState({
    category: searchParams.get("category") || "other",
    goal: searchParams.get("goal") || "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchGoals = async () => {
      try {
        const response = await apiClient.get(`${API_BASE_URL}/api/accounts/my-goals/`);
        setGoals(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch goals for inquiry:", error);
      }
    };

    fetchGoals();
  }, [API_BASE_URL, isLoggedIn]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const message = form.message.trim();
    if (message.length < 5) {
      toast.info("問い合わせ内容を5文字以上で入力してください");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post(`${API_BASE_URL}/api/mypage/inquiries/`, {
        category: form.category,
        goal: form.goal || null,
        message,
      });
      toast.success("問い合わせを受け付けました。返信はマイページに表示されます。");
      setForm({ category: "other", goal: "", message: "" });
      setTimeout(() => navigate("/MyPage"), 900);
    } catch (error) {
      const data = error?.response?.data;
      const messageText =
        typeof data === "string"
          ? data
          : data?.message || data?.detail || data?.message?.[0] || "問い合わせの送信に失敗しました";
      toast.error(messageText);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <main className="contact-page" style={styles.page}>
        <style>{contactPageCss}</style>
        <section style={styles.card}>
          <p style={styles.kicker}>CONTACT</p>
          <h1 style={styles.title}>運営への問い合わせ</h1>
          <p style={styles.text}>
            問い合わせはログインユーザー向けです。返信はメールではなく、マイページに表示されます。
          </p>
          <Link to="/Login" style={styles.primaryLink}>ログインする</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="contact-page" style={styles.page}>
      <style>{contactPageCss}</style>
      <section style={styles.card}>
        <p style={styles.kicker}>CONTACT</p>
        <h1 style={styles.title}>運営への問い合わせ</h1>
        <p style={styles.text}>
          メールアドレスは保存しません。運営からの返信はマイページで確認できます。
        </p>

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>
            問い合わせ種別
            <select
              className="contact-control"
              value={form.category}
              onChange={(event) => setForm({ ...form, category: event.target.value })}
              style={styles.input}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          <label style={styles.label}>
            関連する夢（任意）
            <select
              className="contact-control"
              value={form.goal}
              onChange={(event) => setForm({ ...form, goal: event.target.value })}
              style={styles.input}
            >
              <option value="">選択しない</option>
              {goals.map((goal) => (
                <option key={goal.id} value={goal.id}>{goal.title}</option>
              ))}
            </select>
          </label>

          <label style={styles.label}>
            内容
            <textarea
              className="contact-control"
              value={form.message}
              onChange={(event) => setForm({ ...form, message: event.target.value })}
              placeholder="運営に伝えたいことを書いてください。返信が必要な内容はマイページで返します。"
              rows={7}
              style={{ ...styles.input, resize: "vertical" }}
            />
          </label>

          <button type="submit" disabled={submitting} style={styles.button}>
            {submitting ? "送信中..." : "問い合わせを送信する"}
          </button>
        </form>
      </section>
      <ToastContainer position="top-center" autoClose={3000} />
    </main>
  );
}

const styles = {
  page: {
    minHeight: "80vh",
    background: "#f5ead8",
    color: "#211508",
    padding: "46px 16px 76px",
  },
  card: {
    maxWidth: 720,
    margin: "0 auto",
    backgroundColor: "#ffffff",
    color: "#211508",
    borderRadius: 24,
    padding: "30px 22px",
    boxShadow: "0 16px 38px rgba(43, 31, 18, 0.16)",
    border: "1px solid #d8c7a5",
  },
  kicker: {
    margin: "0 0 8px",
    color: "#9b6500",
    fontWeight: 900,
    letterSpacing: "0.16em",
    fontSize: "0.78rem",
  },
  title: {
    margin: "0 0 10px",
    fontSize: "clamp(1.55rem, 5vw, 2.2rem)",
    color: "#211508",
    fontWeight: 900,
  },
  text: {
    margin: "0 0 22px",
    color: "#4f3920",
    lineHeight: 1.85,
    fontWeight: 600,
  },
  label: {
    display: "block",
    marginTop: 16,
    color: "#24180b",
    fontWeight: 900,
  },
  input: {
    display: "block",
    width: "100%",
    marginTop: 7,
    padding: "12px 13px",
    borderRadius: 12,
    border: "1px solid #b88d45",
    fontSize: 16,
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    color: "#211508",
    WebkitTextFillColor: "#211508",
    caretColor: "#211508",
  },
  button: {
    width: "100%",
    marginTop: 22,
    border: "none",
    borderRadius: 999,
    padding: "13px 18px",
    background: "#211508",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 10px 22px rgba(33, 21, 8, 0.24)",
  },
  primaryLink: {
    display: "inline-block",
    marginTop: 8,
    padding: "11px 19px",
    borderRadius: 999,
    background: "#211508",
    color: "#fff",
    textDecoration: "none",
    fontWeight: 900,
  },
};

const contactPageCss = `
  .contact-page {
    color-scheme: light;
  }

  .contact-page,
  .contact-page label,
  .contact-page p,
  .contact-page h1 {
    color: #211508 !important;
  }

  .contact-page .contact-control,
  .contact-page .contact-control:not(:focus),
  .contact-page .contact-control:focus,
  .contact-page select,
  .contact-page textarea {
    background-color: #ffffff !important;
    color: #211508 !important;
    -webkit-text-fill-color: #211508 !important;
    caret-color: #211508 !important;
    opacity: 1 !important;
  }

  .contact-page .contact-control::placeholder,
  .contact-page textarea::placeholder {
    color: #7b6141 !important;
    -webkit-text-fill-color: #7b6141 !important;
    opacity: 1 !important;
  }

  .contact-page .contact-control option {
    background-color: #ffffff !important;
    color: #211508 !important;
  }

  .contact-page .contact-control:-webkit-autofill,
  .contact-page .contact-control:-webkit-autofill:hover,
  .contact-page .contact-control:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    -webkit-text-fill-color: #211508 !important;
  }
`;

export default Contact;
